import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { CheckCircle2, Loader2, Star } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { usePublicConfig } from "@/lib/site";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const CATEGORIES = ["AI Analysis", "Crypto Data", "Alerts", "Paper Trading", "Strategy Lab", "AI Chat", "Ease of Use", "Subscription/Billing", "Investment Partners", "Bug Report", "Feature Request", "Other"];
const EMPTY = { category: "", pros: "", cons: "", recommendations: "", rating: 0, comments: "", contact_name: "", contact_email: "", website: "" };
const LABELS = ["", "Poor", "Fair", "Good", "Very good", "Excellent"];

const Stars = ({ value, onChange }) => {
  const [hover, setHover] = useState(0);
  const shown = hover || value;
  return (
    <div className="flex items-center gap-1" role="radiogroup" aria-label="Overall experience rating" data-testid="feedback-rating">
      {[1, 2, 3, 4, 5].map((n) => (
        <button key={n} type="button" role="radio" aria-checked={value === n} aria-label={`${n} star${n > 1 ? "s" : ""}`} data-testid={`feedback-star-${n}`}
          onMouseEnter={() => setHover(n)} onMouseLeave={() => setHover(0)} onClick={() => onChange(n)}
          className="p-1 rounded-md transition-transform hover:scale-110 focus:outline-none focus-visible:ring-2 ring-cyan-500">
          <Star className={`w-7 h-7 ${n <= shown ? "fill-amber-400 text-amber-400" : "text-slate-600"}`} />
        </button>
      ))}
      <span className="ml-2 text-sm text-slate-400 min-w-[80px]" data-testid="feedback-rating-label">{LABELS[shown] || "Select a rating"}</span>
    </div>
  );
};

const Field = ({ label, hint, children, optional }) => (
  <label className="block">
    <span className="text-sm text-slate-300">{label} {optional && <span className="text-slate-500 text-xs">(optional)</span>}</span>
    {hint && <span className="block text-xs text-slate-500 mt-0.5">{hint}</span>}
    <div className="mt-1.5">{children}</div>
  </label>
);

const inputCls = "w-full rounded-lg bg-slate-900/80 border border-slate-700 px-3 py-2.5 text-sm text-slate-100 placeholder:text-slate-500 focus:border-cyan-600 outline-none transition-colors";

export const FeedbackDialog = ({ open, preset, onClose }) => {
  const { user } = useAuth();
  const cfg = usePublicConfig();
  const loc = useLocation();
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  useEffect(() => {
    if (open) { setForm({ ...EMPTY, category: preset?.category || "", contact_email: user?.email || "" }); setDone(null); setError(""); }
  }, [open, preset, user]);

  const hasText = [form.pros, form.cons, form.recommendations, form.comments].some((v) => v.trim());
  const valid = form.rating >= 1 && hasText;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.rating) return setError("Please select an overall rating (1–5 stars).");
    if (!hasText) return setError("Please tell us at least one thing you like, would improve, or recommend.");
    setBusy(true);
    try {
      const body = { ...form, category: form.category || "Other", contact_email: form.contact_email.trim() || null, page: loc.pathname.slice(0, 200) };
      const r = await api.post("/feedback", body);
      setDone(r.data.message || "Thank you. Your feedback has been received and will help us improve EV-IDEX.");
    } catch (err) {
      const msg = apiError(err, "Could not send feedback");
      setError(msg);
      toast.error(msg);
    }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !busy) onClose(); }}>
      <DialogContent className="bg-[#0F141C] border-slate-800 text-slate-100 sm:max-w-xl max-h-[92dvh] overflow-y-auto" data-testid="feedback-dialog">
        {done ? (
          <div className="py-8 text-center space-y-4" data-testid="feedback-success">
            <CheckCircle2 className="w-14 h-14 text-emerald-400 mx-auto" />
            <h2 className="font-display text-xl font-bold">Thank you</h2>
            <p className="text-slate-300 leading-relaxed" data-testid="feedback-success-message">{done}</p>
            {form.contact_email && <p className="text-xs text-slate-500">{cfg.response_time}</p>}
            <button type="button" onClick={onClose} data-testid="feedback-close-btn" className="btn-primary rounded-xl px-5 py-2.5 text-sm font-medium">Close</button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-xl">Give Feedback</DialogTitle>
              <DialogDescription className="text-slate-400">Your experience shapes what we build next. Please don't include passwords, card numbers, wallet keys or other sensitive information.</DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4" data-testid="feedback-form">
              <input type="text" name="website" value={form.website} onChange={set("website")} tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" />
              <Field label="Category" optional>
                <select data-testid="feedback-category" value={form.category} onChange={set("category")} className={inputCls}>
                  <option value="">Choose a category…</option>
                  {(cfg.feedback_categories?.length ? cfg.feedback_categories : CATEGORIES).map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
              <Field label="Overall experience rating">
                <Stars value={form.rating} onChange={set("rating")} />
              </Field>
              <Field label="What do you like about EV-IDEX?" hint="Pros">
                <textarea data-testid="feedback-pros" rows={2} value={form.pros} onChange={set("pros")} maxLength={2000} className={inputCls} placeholder="e.g. The scanner explanations are clear and the paper trading helps me practise." />
              </Field>
              <Field label="What could EV-IDEX improve?" hint="Cons">
                <textarea data-testid="feedback-cons" rows={2} value={form.cons} onChange={set("cons")} maxLength={2000} className={inputCls} placeholder="e.g. Alerts could be easier to set up." />
              </Field>
              <Field label="What features or changes would you recommend?">
                <textarea data-testid="feedback-recommendations" rows={2} value={form.recommendations} onChange={set("recommendations")} maxLength={2000} className={inputCls} placeholder="e.g. A weekly summary email of my paper-trading results." />
              </Field>
              <Field label="Additional comments" optional>
                <textarea data-testid="feedback-comments" rows={2} value={form.comments} onChange={set("comments")} maxLength={3000} className={inputCls} />
              </Field>
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Your name" optional>
                  <input data-testid="feedback-contact-name" value={form.contact_name} onChange={set("contact_name")} maxLength={120} className={inputCls} autoComplete="name" />
                </Field>
                <Field label="Email for a reply" optional>
                  <input data-testid="feedback-contact-email" type="email" value={form.contact_email} onChange={set("contact_email")} className={inputCls} autoComplete="email" placeholder="you@example.com" />
                </Field>
              </div>
              <p className="text-[11px] text-slate-500 leading-relaxed">Contact details are optional and used only to reply to you. See our <a href="/privacy#feedback" className="text-cyan-300 hover:underline" target="_blank" rel="noreferrer">Privacy Policy</a>.</p>
              {error && <div className="rounded-lg border border-rose-800/60 bg-rose-950/40 text-rose-200 text-sm px-3 py-2" data-testid="feedback-error" role="alert">{error}</div>}
              <div className="flex justify-end gap-2 pt-1">
                <button type="button" onClick={onClose} disabled={busy} data-testid="feedback-cancel" className="btn-ghost rounded-lg px-4 py-2.5 text-sm">Cancel</button>
                <button type="submit" disabled={busy || !valid} data-testid="feedback-submit" className="btn-primary rounded-lg px-5 py-2.5 text-sm font-semibold disabled:opacity-50 inline-flex items-center gap-2">
                  {busy && <Loader2 className="w-4 h-4 animate-spin" />} Send feedback
                </button>
              </div>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
